/*
  header-nav.js — Restova header navigation custom elements (clean-room, de-vendor project)

  ELEMENTS DEFINED HERE (these names lived ONLY in the removed legacy bundle):
    <header-menu>          desktop top-nav dropdown / mega-menu trigger
    <products-mega-menu>   hover-driven two-pane products mega menu
    <details-modal>        header search modal toggle

  These are live on every page (the header is in header-group.json). All visual
  reveal is NATIVE <details open> + CSS — this script only flips the `open`
  attribute, toggles `products-mega-menu__item--active`, manages aria/scroll-lock,
  and seeds the mega-menu height CSS var. Re-read sections/header.liquid +
  snippets/products-mega-menu.liquid + base.css before changing any selector.

  LOAD CONTRACT (load-bearing)
  - CLASSIC script (NOT a module). Must load AFTER base-classes.js (which provides
    the global `DetailsDisclosure` base class that <header-menu> extends by BARE
    NAME) and after theme-globals.js (which provides the global trapFocus /
    removeTrapFocus helpers). Header-group emits this with `defer`, so document
    order guarantees those globals are evaluated first.
  - EVERY customElements.define below is guarded with if(!customElements.get(...))
    so a theme-editor section re-render can re-run this file without throwing.

  CROSS-FILE CONTRACTS (do not break):
  - <header-menu>.close() (inherited from DetailsDisclosure) is bulk-called by
    StickyHeader.closeMenuDisclosure() in sections/header.liquid (~L874).
  - <header-menu> writes the `.preventHide` EXPANDO PROPERTY on .header-wrapper,
    read by StickyHeader.onScroll (header.liquid ~L817 `if (this.preventHide) return;`).
  - <header-menu> seeds documentElement style var `--header-bottom-position-desktop`,
    consumed by base.css ~L9432 to cap .mega-menu__content max-height.
  - <details-modal>.close(focusToggle) is called by StickyHeader.closeSearchModal()
    as `.close(false)` (header.liquid ~L879) — a FALSY arg must suppress focus return.
  - <products-mega-menu> reaches across the document to close all
    [id^="Details-HeaderMenu"] and [id^="ProductsMegaMenu-"] for mutual exclusion;
    those id prefixes are a HARD contract with header.liquid + products-mega-menu.liquid.
*/

/* ------------------------------------------------------------------ *
 * <header-menu> — desktop nav dropdown / mega-menu trigger.
 * Extends the global DetailsDisclosure (from base-classes.js).
 * ------------------------------------------------------------------ */
class HeaderMenu extends DetailsDisclosure {
  constructor() {
    // DetailsDisclosure's constructor wires the focusout + toggle listeners on
    // the inner <details> and caches mainDetailsToggle / content.
    super();

    // The outer sticky/static header carries class .header-wrapper. May be null
    // on unusual layouts — every read below is guarded.
    this.header = document.querySelector('.header-wrapper');
  }

  /*
    OVERRIDE — intentionally does NOT call super.onToggle().
    Header dropdown / mega reveal relies purely on CSS [open] transitions, so we
    must NOT run the base class's WAAPI play()/cancel() (that would double-manage
    the animation). We only:
      (1) keep the sticky header from auto-hiding while a menu is open, and
      (2) seed the mega-menu max-height CSS var once.
  */
  onToggle() {
    if (!this.header) return;

    // Sticky header reads this exact expando property name.
    this.header.preventHide = this.mainDetailsToggle.open;

    // Seed the height cap ONCE (matches legacy: guarded on empty string).
    if (
      document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === ''
    ) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
      );
    }
  }

  // close() is inherited from DetailsDisclosure (removes [open] + sets summary
  // aria-expanded='false'); StickyHeader depends on that name/behavior.
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/* ------------------------------------------------------------------ *
 * <products-mega-menu> — hover-driven two-pane products mega menu.
 * Left column = child links; right column = the hovered item's cards.
 * ------------------------------------------------------------------ */
class ProductsMegaMenu extends HTMLElement {
  constructor() {
    super();

    // Bound handlers kept as instance refs so disconnectedCallback can detach
    // the document-level listener (the legacy version leaked it on every
    // editor section re-render).
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.outsideClick = this.outsideClick.bind(this);
    this.displayContent = this.displayContent.bind(this);

    this._initialized = false;
  }

  connectedCallback() {
    // Idempotent — sections re-render in the editor.
    if (this._initialized) return;

    this.details = this.querySelector('details[id^="ProductsMegaMenu-"]');
    this.mainLink = this.querySelector('summary.header__menu-item');
    this.body = this.querySelector('.products-mega-menu__body');
    this.overlay = this.querySelector('.products-mega-menu__overlay');
    this.items = this.querySelectorAll('[id^="ProductsMegaMenu-Item"]');
    this.links = this.querySelectorAll('[id^="ProductsMegaMenu-Link"]');

    // If the markup isn't present (snippet only renders when a nav link is in
    // products_mega_menu_links), instantiate as a no-op.
    if (!this.details || !this.mainLink) return;

    this.mainLink.addEventListener('mouseover', this.openMenu);

    if (this.overlay) {
      this.overlay.addEventListener('mouseover', this.closeMenu);
      this.overlay.addEventListener('click', this.closeMenu);
    }

    document.addEventListener('click', this.outsideClick);

    this.links.forEach((link) => {
      link.addEventListener('mouseover', this.displayContent);
    });

    this._initialized = true;
  }

  disconnectedCallback() {
    // Detach the document listener + element listeners (leak fix vs legacy).
    document.removeEventListener('click', this.outsideClick);

    if (this.mainLink) this.mainLink.removeEventListener('mouseover', this.openMenu);
    if (this.overlay) {
      this.overlay.removeEventListener('mouseover', this.closeMenu);
      this.overlay.removeEventListener('click', this.closeMenu);
    }
    if (this.links) {
      this.links.forEach((link) => link.removeEventListener('mouseover', this.displayContent));
    }

    this._initialized = false;
  }

  /* Open this mega menu and close every OTHER header dropdown + mega menu
     (mutual exclusion across the whole header). */
  openMenu() {
    document
      .querySelectorAll('[id^="Details-HeaderMenu"], [id^="ProductsMegaMenu-"]')
      .forEach((el) => el.removeAttribute('open'));
    this.details.setAttribute('open', '');
  }

  closeMenu() {
    this.details.removeAttribute('open');
  }

  /* Close on clicks outside the panel body (the overlay has its own close
     listener, so it never routes here — body.contains(overlay) is true). */
  outsideClick(event) {
    if (!this.details.hasAttribute('open')) return;
    if (event.target === this.body) return;
    if (!this.body.contains(event.target)) this.closeMenu();
  }

  /* Switch the active right-pane to the hovered left link's item. event.target
     is often a nested node (span / caret svg / preview img), so resolve the
     owning <li> via closest() — never read target.classList directly. */
  displayContent(event) {
    const item = event.target.closest('[id^="ProductsMegaMenu-Item"]');
    if (!item) return;

    this.items.forEach((el) => el.classList.remove('products-mega-menu__item--active'));
    item.classList.add('products-mega-menu__item--active');
  }
}

if (!customElements.get('products-mega-menu')) {
  customElements.define('products-mega-menu', ProductsMegaMenu);
}

/* ------------------------------------------------------------------ *
 * <details-modal> — header search modal toggle.
 * Turns the search <summary> into a button-role dialog: opens/closes the
 * search dialog, traps focus, locks body scroll, closes on Escape /
 * outside-click / overlay-click / explicit close button.
 * ------------------------------------------------------------------ */
class DetailsModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    // Bound body-click handler, attached only while open (see open/close).
    this.onBodyClick = this.onBodyClick.bind(this);

    if (!this.detailsContainer || !this.summaryToggle) return;

    // Escape closes (default close(true) returns focus to the summary).
    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    });

    // Fully JS-control the toggle (preventDefault on the summary click).
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));

    // The modal close button. Receives the MouseEvent as its arg -> truthy ->
    // close(true) returns focus to the summary. (Only outside/overlay click and
    // StickyHeader pass an explicit false.)
    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  disconnectedCallback() {
    // If the element is torn down while open, drop the body listener + scroll
    // lock so they don't leak onto a detached node (legacy left these dangling).
    document.body.removeEventListener('click', this.onBodyClick);
    if (this.detailsContainer && this.detailsContainer.hasAttribute('open')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    const details = event.target.closest('details');
    details.hasAttribute('open') ? this.close() : this.open(event);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick;
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    // Focus trap is best-effort — guard the global in case it isn't loaded yet.
    if (typeof trapFocus === 'function') {
      trapFocus(
        this.detailsContainer.querySelector('[tabindex="-1"]'),
        this.detailsContainer.querySelector('input:not([type="hidden"])')
      );
    }
  }

  // returnFocus truthy -> focus returns to the summary; falsy -> no focus move
  // (used by outside/overlay click and StickyHeader.closeSearchModal()).
  close(returnFocus = true) {
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(returnFocus ? this.summaryToggle : null);
    }
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) {
      this.close(false);
    }
  }
}

if (!customElements.get('details-modal')) {
  customElements.define('details-modal', DetailsModal);
}

/* Belt-and-suspenders globals (the class declarations above are already global
   lexical bindings in this classic script; these mirror the de-vendor pattern
   for any consumer that reaches for window.X). */
window.HeaderMenu = HeaderMenu;
window.ProductsMegaMenu = ProductsMegaMenu;
window.DetailsModal = DetailsModal;
